import { FormHandler } from '@droplet-tech-code/core-elements/module/ui/Inputs/Form';
import {
  Modal,
  useModal,
  useModalVerify,
} from '@droplet-tech-code/core-elements/module/ui/Modal';
import { HStack } from '@droplet-tech-code/core-elements/module/ui/Stack';
import { Text } from '@droplet-tech-code/core-elements/module/ui/Text';
import { Toggle } from '@droplet-tech-code/core-elements/module/ui/Toggle/Toggle';
import { handleResponse } from '@droplet-tech-code/core-elements/module/utils/error';
import {
  PurchaseClientCancelCustomerOptions,
  PurchaseClientCancelProviderOptionsExternal,
} from '@naus-code/naus-admin-types';
import { MutableRefObject, useState } from 'react';

import { translate } from '~/utils/translation.utils';
import { getPriceWithSymbol } from '~/screens/Support/Purchases/purchase.utils';

import { purchaseActionApi } from '../../PurchaseActions.api';
import { CancellationMethod, convertCompanyCcyToCustomerCcy } from './Cancel.utils';
import {
  cancelBookingManager,
  CancelBookingPropsResolved,
  fanOutGroupValueToPurchases,
  ModalActionPanelCancelBookingKey,
  ProviderGroup,
} from './CancelBooking.utils';
import {
  actualRefundFieldId,
  expectedRefundFieldId,
} from './CancelBooking.Form';

const getCancelHook = (method: CancellationMethod | undefined) => {
  switch (method) {
    case 'customer':
      return purchaseActionApi.useCancelPurchaseCustomerMutation;
    case 'provider':
      return purchaseActionApi.useCancelPurchaseProviderMutation;
    case 'visual':
      return purchaseActionApi.useCancelPurchaseVisualMutation;
    case 'customer_and_provider':
    default:
      return purchaseActionApi.useCancelPurchaseProviderAndCustomerMutation;
  }
};

export const CancelBookingFooter = ({
  formHandlerRef,
  providerGroups,
  isCalcInFlight,
  ...props
}: CancelBookingPropsResolved & {
  formHandlerRef: MutableRefObject<FormHandler>;
  providerGroups: ProviderGroup[];
  isCalcInFlight?: boolean;
}) => {
  const [silentOn, setSilentOn] = useState(false);
  const { closeModal } = useModal();
  const [cancelMode] = cancelBookingManager.state.useGlobalState('type');
  const [selectedTicketsByPurchase] =
    cancelBookingManager.state.useGlobalState('selectedTicketsByPurchase');
  const [doCancel, { isLoading }] = getCancelHook(cancelMode)();
  const { verifyAction } = useModalVerify();

  const purchaseIdsAll = providerGroups.flatMap((g) =>
    g.purchases.map((p) => p._id),
  );
  const ticketIdsAll = purchaseIdsAll.flatMap(
    (pid) => selectedTicketsByPurchase[pid] ?? [],
  );

  const totalSelectedTickets = ticketIdsAll.length;
  const disabled =
    !cancelMode || totalSelectedTickets === 0 || !!isCalcInFlight;

  // voidCategory: per Q8 we apply the same to all purchases. Use the first
  // selected purchase's category, falling back to GENERAL.
  const firstSelectedPurchase = providerGroups
    .flatMap((g) => g.purchases)
    .find((p) => (selectedTicketsByPurchase[p._id]?.length ?? 0) > 0);
  const voidCategory = firstSelectedPurchase?.void_category ?? 'GENERAL';

  const buildCustomerOptions = (
    value: any,
  ): PurchaseClientCancelCustomerOptions => {
    const isExternal = value.type === 'external';
    const expectedByCode: { [code: string]: number } = {};
    for (const g of providerGroups) {
      const actual = Number(value[actualRefundFieldId(g.providerReservationCode)]) || 0;
      expectedByCode[g.providerReservationCode] = isExternal
        ? actual
        : Number(value[expectedRefundFieldId(g.providerReservationCode)]) || 0;
    }
    // Group refund fields are entered in company ccy; customerOptions must
    // carry customer-ccy amounts (providerOptions keeps company ccy).
    const expectedInCompanyCcy = fanOutGroupValueToPurchases(
      providerGroups,
      expectedByCode,
    );
    const expectedRefundFromCompanyByPurchase: { [purchaseId: string]: number } = {};
    for (const g of providerGroups) {
      for (const p of g.purchases) {
        expectedRefundFromCompanyByPurchase[p._id] = convertCompanyCcyToCustomerCcy(
          expectedInCompanyCcy[p._id],
          p,
        );
      }
    }
    return {
      refundMethod: value.refundMethod,
      serviceFeeInBookCcy: Number(value.serviceFeeInBookCcy) || 0,
      customerRefundInBookCcy: Number(value.customerRefundInBookCcy) || 0,
      expectedRefundFromCompanyByPurchase,
    };
  };

  const buildProviderOptions = (
    value: any,
  ): PurchaseClientCancelProviderOptionsExternal => {
    const isExternal = value.type === 'external';
    const expectedByCode: { [code: string]: number } = {};
    const actualByCode: { [code: string]: number } = {};
    for (const g of providerGroups) {
      const actual = Number(value[actualRefundFieldId(g.providerReservationCode)]) || 0;
      expectedByCode[g.providerReservationCode] = isExternal
        ? actual
        : Number(value[expectedRefundFieldId(g.providerReservationCode)]) || 0;
      actualByCode[g.providerReservationCode] = actual;
    }
    return {
      type: value.type,
      cancelDate: value.cancelDate,
      expectedRefundFromCompanyByPurchase: fanOutGroupValueToPurchases(
        providerGroups,
        expectedByCode,
      ),
      actualRefundFromCompanyByPurchase: fanOutGroupValueToPurchases(
        providerGroups,
        actualByCode,
      ),
    };
  };

  const submit = async (value: any) => {
    const cancelOptions = {
      purchaseIds: purchaseIdsAll.filter(
        (pid) => (selectedTicketsByPurchase[pid]?.length ?? 0) > 0,
      ),
      ticketIds: ticketIdsAll,
      voidCategory,
      silentUpdate: silentOn,
    };

    const supportId = props.supportRequest._id;
    let payload: any;
    switch (cancelMode) {
      case 'visual':
        payload = { cancelOptions, supportId };
        break;
      case 'customer':
        payload = {
          cancelOptions,
          customerOptions: buildCustomerOptions(value),
          supportId,
        };
        break;
      case 'provider':
        payload = {
          cancelOptions,
          providerOptions: buildProviderOptions(value),
          supportId,
        };
        break;
      case 'customer_and_provider':
      default:
        payload = {
          cancelOptions,
          customerOptions: buildCustomerOptions(value),
          providerOptions: buildProviderOptions(value),
          supportId,
        };
        break;
    }

    const response = await doCancel(payload);
    handleResponse({
      response,
      onSuccess: () => {
        closeModal(ModalActionPanelCancelBookingKey);
      },
    });
  };

  return (
    <Modal.Footer
      divider
      isLoading={isLoading}
      confirm={translate('purchases.purchaseTool.processCancellation')}
      confirmProps={{ disabled }}
      onConfirm={async () => {
        if (!cancelMode) return;
        const { value, valid } = formHandlerRef.current.getValue<any>();
        if (!valid) return;

        // For modes that involve a customer-side service fee, ask for verification.
        const needsServiceFeeConfirm =
          cancelMode === 'customer' || cancelMode === 'customer_and_provider';

        if (needsServiceFeeConfirm) {
          const verify = await verifyAction({
            title: translate('supportRequests.verifyServiceFee'),
            description:
              translate('supportRequests.serviceFeeMakeSure') +
              '\n\nService fee: ' +
              getPriceWithSymbol(
                Number(value.serviceFeeInBookCcy) || 0,
                providerGroups[0]?.purchases[0]?.ccySymbol ?? '',
              ),
          });
          if (!verify) return;
        }

        await submit(value);
      }}
    >
      <HStack space="1">
        <Toggle
          color="primary-mid"
          value={silentOn}
          onChange={(v) => setSilentOn(v)}
        />
        <Text.Body2Regular color="monochrome-extraDark">
          {translate('purchases.purchaseTool.purchaseCompare.silentUpdate')}
        </Text.Body2Regular>
      </HStack>
    </Modal.Footer>
  );
};
